import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { KpiCard } from '../components/common/KpiCard';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Package, AlertTriangle, Plus, Pencil, Trash2, ArrowUpDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { useConfirm } from '../context/ConfirmContext';

export default function InventoryPage() {
  const { t, i18n } = useTranslation();
  const [ingredients, setIngredients] = useState([]);
  const [movements, setMovements] = useState([]);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const blank = { name: '', unit: 'pz', stock: 0, min_stock: 0, cost_per_unit: 0 };
  const [f, setF] = useState(blank);
  const [adjDelta, setAdjDelta] = useState('');
  const confirm = useConfirm();

  const load = async () => {
    const [i, m] = await Promise.all([api.get('/ingredients'), api.get('/inventory/movements')]);
    setIngredients(i.data); setMovements(m.data);
  };
  useEffect(() => { load(); }, []);

  const lowStock = ingredients.filter((i) => Number(i.stock) <= Number(i.min_stock));
  const filtered = ingredients.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));

  const save = async () => {
    if (!f.name.trim()) { toast.error(t('inventory.nameRequired')); return; }
    const payload = { ...f, stock: Number(f.stock), min_stock: Number(f.min_stock), cost_per_unit: Number(f.cost_per_unit) };
    if (editing) await api.put(`/ingredients/${editing.id}`, payload);
    else await api.post('/ingredients', payload);
    setDialog(false); setEditing(null); setF(blank); load(); toast.success(t('inventory.ingredientSaved'));
  };
  const del = async (i) => { if (await confirm({ title: t('inventory.deleteConfirm', { name: i.name }), destructive: true, confirmText: t('inventory.delete') })) { await api.delete(`/ingredients/${i.id}`); load(); } };
  const doAdjust = async () => {
    const d = Number(adjDelta);
    if (!d) { toast.error(t('inventory.enterQty')); return; }
    await api.post(`/ingredients/${adjust.id}/adjust`, { delta: d, reason: t('inventory.adjustReason') });
    setAdjust(null); setAdjDelta(''); load(); toast.success(t('inventory.stockUpdated'));
  };

  return (
    <div className="p-4 md:p-6">
      <Tabs defaultValue="stock">
        {/* Toggle Inventario/Movimenti allineato sulla stessa linea del pulsante +Ingrediente. */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <TabsList><TabsTrigger value="stock">{t('inventory.tabStock')}</TabsTrigger><TabsTrigger value="movements">{t('inventory.tabMovements')}</TabsTrigger></TabsList>
          <Button className="rounded-xl" data-testid="add-ingredient-button" onClick={() => { setEditing(null); setF(blank); setDialog(true); }}><Plus className="h-4 w-4 mr-2" /> {t('inventory.ingredientBtn')}</Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <KpiCard label={t('inventory.totalIngredients')} value={ingredients.length} icon={Package} accent="primary" />
          <KpiCard label={t('inventory.lowStock')} value={lowStock.length} icon={AlertTriangle} accent="bill" />
        </div>

        {lowStock.length > 0 && (
          <div className="rounded-xl border border-pos-bill/40 bg-pos-bill/5 p-3 mb-4 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-pos-bill shrink-0 mt-0.5" />
            <div className="text-sm"><span className="font-medium">{t('inventory.warning')}</span> {lowStock.length === 1 ? t('inventory.lowStockAlertOne', { names: lowStock.map((i) => i.name).join(', ') }) : t('inventory.lowStockAlertMany', { names: lowStock.map((i) => i.name).join(', ') })}</div>
          </div>
        )}

        <TabsContent value="stock" className="mt-4">
          <Card className="p-4">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('inventory.searchIngredient')} className="max-w-xs mb-3" data-testid="inventory-search-input" />
            <Table>
              <TableHeader><TableRow><TableHead>{t('inventory.colIngredient')}</TableHead><TableHead>{t('inventory.colStock')}</TableHead><TableHead>{t('inventory.colMin')}</TableHead><TableHead>{t('inventory.colStatus')}</TableHead><TableHead className="text-right">{t('inventory.colActions')}</TableHead></TableRow></TableHeader>
              <TableBody>
                {filtered.map((i) => {
                  const low = Number(i.stock) <= Number(i.min_stock);
                  return (
                    <TableRow key={i.id}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell className="tabular">{i.stock} {i.unit}</TableCell>
                      <TableCell className="tabular text-muted-foreground">{i.min_stock} {i.unit}</TableCell>
                      <TableCell>{low ? <span data-testid="inventory-low-stock-badge" className="text-xs px-2 py-1 rounded-full bg-pos-bill/10 text-pos-bill font-medium">{t('inventory.lowStockBadge')}</span> : <span className="text-xs px-2 py-1 rounded-full bg-pos-free/10 text-pos-free font-medium">{t('inventory.ok')}</span>}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" data-testid="inventory-adjust-button" onClick={() => { setAdjust(i); setAdjDelta(''); }}><ArrowUpDown className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(i); setF(i); setDialog(true); }}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => del(i)}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="movements" className="mt-4">
          <Card className="p-4">
            <Table>
              <TableHeader><TableRow><TableHead>{t('inventory.colDate')}</TableHead><TableHead>{t('inventory.colIngredient')}</TableHead><TableHead>{t('inventory.colChange')}</TableHead><TableHead>{t('inventory.colReason')}</TableHead><TableHead className="text-right">{t('inventory.colStock')}</TableHead></TableRow></TableHeader>
              <TableBody>
                {movements.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-sm text-muted-foreground">{new Date(m.created_at).toLocaleString(i18n.language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</TableCell>
                    <TableCell>{m.ingredient_name}</TableCell>
                    <TableCell className={cn('tabular font-medium', m.delta < 0 ? 'text-destructive' : 'text-pos-free')}>{m.delta > 0 ? '+' : ''}{m.delta}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.reason}</TableCell>
                    <TableCell className="text-right tabular">{m.resulting_stock}</TableCell>
                  </TableRow>
                ))}
                {movements.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t('inventory.noMovements')}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? t('inventory.editIngredient') : t('inventory.newIngredient')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t('inventory.name')}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="ingredient-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('inventory.unit')}</Label>
                <Select value={f.unit} onValueChange={(v) => setF({ ...f, unit: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{['pz', 'g', 'kg', 'ml', 'l'].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t('inventory.unitCost')}</Label><Input type="number" value={f.cost_per_unit} onChange={(e) => setF({ ...f, cost_per_unit: e.target.value })} className="mt-1.5" /></div>
              <div><Label>{t('inventory.currentStock')}</Label><Input type="number" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} className="mt-1.5" /></div>
              <div><Label>{t('inventory.minStock')}</Label><Input type="number" value={f.min_stock} onChange={(e) => setF({ ...f, min_stock: e.target.value })} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>{t('inventory.cancel')}</Button><Button onClick={save} data-testid="ingredient-save-button">{t('inventory.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('inventory.adjustStock')} — {adjust?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{t('inventory.currentStockLabel')} <span className="font-medium text-foreground">{adjust?.stock} {adjust?.unit}</span></div>
            <div><Label>{t('inventory.changeLabel')}</Label><Input type="number" value={adjDelta} onChange={(e) => setAdjDelta(e.target.value)} placeholder={t('inventory.changePlaceholder')} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAdjust(null)}>{t('inventory.cancel')}</Button><Button onClick={doAdjust}>{t('inventory.apply')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
